<template>
  <div class="shell" ref="shellRef">
    <div class="dots"></div>

    <!-- NAV -->
    <nav class="site-nav">
      <div class="nav-left">
        <div class="brand-mark">M</div>
        <div class="brand-stack">
          <span class="brand-name">Milo Metaute</span>
          <span class="brand-role">Product Designer</span>
        </div>
      </div>

      <div class="nav-center" role="tablist">
        <button
          v-for="(tab, i) in navTabs"
          :key="i"
          class="nav-tab"
          :class="{ active: activeStrip === i }"
          @click="navSelectStrip(i)"
        >{{ tab }}</button>
      </div>

      <div class="nav-right">
        <span class="badge">
          <span class="badge-dot"></span>
          Focused at Eden
        </span>
      </div>
    </nav>

    <!-- LEFT RAIL -->
    <aside class="rail rail-l" ref="railLRef" @mouseleave="hideScrubber('l')">
      <div class="rail-head">Carrera</div>
      <div
        v-for="year in years"
        :key="'l-' + year"
        class="yr"
        :class="{ has: !!timelineEvents[year] }"
        :data-year="year"
        :style="timelineEvents[year] ? { '--dot-c': timelineEvents[year].accent } : {}"
        @mouseenter="e => onYearEnter(e, year, 'left', 'l')"
        @mouseleave="() => onYearLeave(year)"
      >
        <span class="yr-tick"></span>
        <span class="yr-label">{{ year }}</span>
        <span v-if="timelineEvents[year]" class="yr-dot"></span>
      </div>
      <div class="scrubber" :class="{ show: scrubL.show }" :style="{ top: scrubL.top + 'px' }"></div>
    </aside>

    <!-- RIGHT RAIL -->
    <aside class="rail rail-r" ref="railRRef" @mouseleave="hideScrubber('r')">
      <div class="rail-head">Exp.</div>
      <div
        v-for="year in years"
        :key="'r-' + year"
        class="yr"
        :class="{ has: !!timelineEvents[year] }"
        :data-year="year"
        :style="timelineEvents[year] ? { '--dot-c': timelineEvents[year].accent } : {}"
        @mouseenter="e => onYearEnter(e, year, 'right', 'r')"
        @mouseleave="() => onYearLeave(year)"
      >
        <span class="yr-tick"></span>
        <span class="yr-label">{{ year - 2010 }}</span>
        <span v-if="timelineEvents[year]" class="yr-dot"></span>
      </div>
      <div class="scrubber" :class="{ show: scrubR.show }" :style="{ top: scrubR.top + 'px' }"></div>
    </aside>

    <!-- TOOLTIP -->
    <div
      class="tip"
      :class="[tipData.side, { show: tipData.show }]"
      :style="tipData.style"
    >
      <div v-if="tipData.event" class="tip-card">
        <span class="tip-tag" :style="{ background: resolveAccent(tipData.event.accent), color: '#fff' }">
          {{ tipData.event.scope }}
        </span>
        <div class="tip-head">
          <span class="tip-co">{{ tipData.event.company }}</span>
          <span class="tip-scope-badge">{{ tipData.event.tag }}</span>
        </div>
        <div class="tip-role">{{ tipData.event.role }}</div>
        <div class="tip-years">{{ tipData.event.years }}</div>
        <p class="tip-desc">{{ tipData.event.desc }}</p>
        <div class="tip-meta">
          <div class="cell">
            <span class="k">Impacto</span>
            <span class="v">{{ tipData.event.impact }}</span>
          </div>
          <div class="cell">
            <span class="k">Stack</span>
            <span class="v">{{ tipData.event.stack }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- HERO -->
    <section class="hero">
      <span class="eyebrow">
        <span class="eb-dot"></span>
        Medellín, CO
        <span class="eb-dot"></span>
      </span>

      <div class="avatar-ring">
        <img class="avatar-inner" :src="miloProfile" alt="Camilo Metaute" />
      </div>

      <h1 class="headline">
        <span class="h-line1">+15 años diseñando</span>
        <span class="h-line2" :style="{ color: currentAccent, transition: 'color .3s ease' }">
          <span>{{ displayText }}</span><span class="tcursor" :style="{ background: currentAccent }"></span>
        </span>
      </h1>

      <p class="subtitle">
        Convierto problemas complejos en experiencias digitales claras.
        Trabajo en la intersección de estrategia, diseño y sistemas.
      </p>

      <div class="cta-row">
        <button class="btn-primary" @click="scrollToWork">
          ver mi trabajo
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <a class="btn-outline" href="https://www.linkedin.com/in/milometaute/" target="_blank" rel="noopener noreferrer">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M1 7.5a6 6 0 0 0 12 0 6 6 0 0 0-12 0z" stroke="currentColor" stroke-width="1.4"/>
            <circle cx="7" cy="7.5" r="2" stroke="currentColor" stroke-width="1.4"/>
          </svg>
          ver mi CV
        </a>
      </div>

      <span class="focused">
        <span class="chip" :style="{ color: currentAccent, transition: 'color .3s ease' }">
          <span class="cdot"></span>
          <span>{{ currentFocus }}</span>
        </span>
      </span>

      <div class="proof">
        <div class="avatars">
          <div class="av av-a">AC</div>
          <div class="av av-b">LM</div>
          <div class="av av-c">SR</div>
          <div class="av av-d">JP</div>
        </div>
        <p class="proof-text">He trabajado con <b>startups y empresas</b> en LATAM</p>
      </div>
    </section>

    <!-- SECTION STRIP -->
    <div class="strip" ref="stripRef">
      <div class="strip-underline" :style="underlineStyle"></div>
      <div
        v-for="(item, i) in stripItems"
        :key="i"
        class="strip-item"
        :class="{ active: activeStrip === i }"
        @click="selectStrip(i)"
      >
        <div class="si-ic" :class="item.color" v-html="item.icon"></div>
        <div class="si-text">
          <span class="t1">{{ item.title }}</span>
          <span class="t2">{{ item.sub }}</span>
        </div>
        <svg class="si-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M5 2l5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <!-- PROJECTS SECTION -->
    <section class="projects-section" v-show="activeStrip === 0">
      <!-- Section header -->
      <div class="ps-header">
        <div class="ps-header-left">
          <span class="ps-label">Proyectos</span>
          <h2 class="ps-title">Casos recientes</h2>
        </div>
        <p class="ps-desc">Selección de trabajo profesional — problemas reales, equipos reales, resultados medibles.</p>
      </div>

      <!-- Projects grid -->
      <div class="projects-grid">
        <article
          v-for="(project, i) in projects"
          :key="i"
          class="project-card"
          @click="$router.push('/project/' + project.slug)"
        >
          <!-- Visual area -->
          <div class="pc-visual" :style="{ background: project.visualBg }">
            <div class="pc-visual-dots"></div>
            <div class="pc-visual-label">
              <span class="pc-company-tag" :style="{ color: project.accent }">{{ project.company }}</span>
            </div>
            <div class="pc-visual-year">{{ project.period }}</div>
          </div>

          <!-- Card body -->
          <div class="pc-body">
            <div class="pc-meta">
              <span class="pc-company">{{ project.company }}</span>
              <span class="pc-sep">·</span>
              <span class="pc-period">{{ project.period }}</span>
            </div>
            <h3 class="pc-title">{{ project.title }}</h3>
            <p class="pc-desc">{{ project.desc }}</p>
            <div class="pc-tags">
              <span v-for="tag in project.tags" :key="tag" class="pc-tag" :style="{ color: project.accent, background: project.accentBg }">{{ tag }}</span>
            </div>
          </div>

          <!-- Hover arrow -->
          <div class="pc-arrow">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </article>
      </div>
    </section>

    <!-- EXPLORATIONS SECTION -->
    <section class="projects-section" v-show="activeStrip === 1">
      <!-- Section header -->
      <div class="ps-header">
        <div class="ps-header-left">
          <span class="ps-label">Taller</span>
          <h2 class="ps-title">Exploraciones en código</h2>
        </div>
        <p class="ps-desc">En mi tiempo libre disfruto crear componentes en código — este es mi espacio para experimentar.</p>
      </div>

      <!-- Explorations grid -->
      <div class="projects-grid">
        <article
          v-for="(item, i) in explorations"
          :key="i"
          class="project-card"
          @click="$router.push('/exploration/' + item.slug)"
        >
          <!-- Visual area: thumbnail -->
          <div class="pc-visual pc-visual--thumb" :style="{ background: item.accentBg }">
            <img :src="item.thumbnail" :alt="item.title" class="pc-thumb-img" />
          </div>

          <!-- Card body -->
          <div class="pc-body">
            <div class="pc-meta">
              <span class="pc-company">{{ item.category }}</span>
              <span class="pc-sep">·</span>
              <span class="pc-period">{{ item.tech }}</span>
            </div>
            <h3 class="pc-title">{{ item.title }}</h3>
            <p class="pc-desc">{{ item.shortDesc || item.desc }}</p>
          </div>

          <!-- Hover arrow -->
          <div class="pc-arrow">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </article>
      </div>
    </section>

    <!-- SOBRE MILO SECTION -->
    <section class="about-section" v-show="activeStrip === 2">

      <!-- Header: foto + nombre + rol + stats -->
      <div class="ab-header">
        <div class="ab-header-identity">
          <img :src="miloProfile" class="ab-photo" alt="Camilo Metaute" />
          <div class="ab-header-text">
            <span class="ps-label">Sobre Milo</span>
            <h2 class="ab-name">Camilo Metaute</h2>
            <p class="ab-role">Senior Product Designer · Medellín, Colombia</p>
          </div>
        </div>
        <div class="ab-stats">
          <div class="ab-stat">
            <span class="ab-stat-val">+15</span>
            <span class="ab-stat-label">Años de experiencia</span>
          </div>
          <div class="ab-stat">
            <span class="ab-stat-val">2</span>
            <span class="ab-stat-label">Startups co-fundadas</span>
          </div>
          <div class="ab-stat">
            <span class="ab-stat-val">8+</span>
            <span class="ab-stat-label">Productos lanzados</span>
          </div>
        </div>
      </div>

      <div class="ab-divider"></div>

      <!-- Philosophy grid 2×2 -->
      <div class="ab-grid">
        <div v-for="(block, i) in aboutBlocks" :key="i" class="ab-block">
          <span class="ab-num">0{{ i + 1 }}</span>
          <h3 class="ab-block-title">{{ block.title }}</h3>
          <p class="ab-block-body">{{ block.body }}</p>
        </div>
      </div>

    </section>
  </div>
</template>

<script>
import miloProfile from '@/assets/milo-hero-section.png';
import { projects } from '@/data/projects.js';
import { explorations } from '@/data/explorations.js';

export default {
  name: 'HomePage',

  data() {
    return {
      miloProfile,
      navTabs: ['Trabajo', 'Exploración', 'Sobre mí'],
      activeTab: 0,

      phrases: [
        { text: 'simplicidad en entornos de alta complejidad.',         color: 'var(--milo-indigo)', focus: 'Diseño · Desarrollo' },
        { text: 'productos que llegan a manos de usuarios reales.',    color: 'var(--milo-forest)', focus: 'Visión de Impacto' },
        { text: 'formas de validar antes de saltar al código.',        color: 'var(--milo-terra)',  focus: 'Validación real' }
      ],
      phraseIndex: 0,
      charIndex: 0,
      deleting: false,
      displayText: '',
      currentAccent: 'var(--milo-indigo)',
      currentFocus: 'Diseño · Desarrollo',
      typeTimer: null,

      scrubL: { show: false, top: 0 },
      scrubR: { show: false, top: 0 },

      tipData: { show: false, event: null, side: 'left', style: {} },
      tipHideTimer: null,

      activeStrip: parseInt(this.$route.query.strip) || 0,
      underlineStyle: {},

      stripItems: [
        {
          color: 'indigo',
          title: 'Diseño en el trabajo',
          sub: 'Proyectos profesionales · 14',
          icon: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="5" height="5" rx="1.2" stroke="currentColor" stroke-width="1.2"/><rect x="8" y="1" width="5" height="5" rx="1.2" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="8" width="5" height="5" rx="1.2" stroke="currentColor" stroke-width="1.2"/><rect x="8" y="8" width="5" height="5" rx="1.2" stroke="currentColor" stroke-width="1.2"/></svg>'
        },
        {
          color: 'forest',
          title: 'Taller de exploraciones',
          sub: 'Experimentos · side projects',
          icon: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.2"/><path d="M7 4v3l2 1.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>'
        },
        {
          color: 'steel',
          title: 'Sobre Milo',
          sub: '15 años · Medellín, CO',
          icon: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2a3 3 0 100 6 3 3 0 000-6zM2 13c0-2.5 2.2-4.5 5-4.5S12 10.5 12 13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>'
        }
      ],

      projects,
      explorations,

      aboutBlocks: [
        {
          title: 'Más allá del lienzo',
          body: 'Mi trabajo no termina en Figma; termina cuando el flujo es fluido y predecible. Con +15 años simplificando procesos en sectores de alta complejidad aprendí a identificar patrones de fondo sin importar el sector. Mi enfoque es transformar la densidad técnica en soluciones que el negocio puede escalar.'
        },
        {
          title: 'El Código como Material',
          body: 'No veo el diseño y el desarrollo como islas. Trato el CSS y el HTML como materiales de construcción. Esto me permite crear prototipos de alta fidelidad que no solo "se ven" reales, sino que permiten validar decisiones de negocio con usuarios antes de que Tech escriba la primera línea de código.'
        },
        {
          title: 'Disciplina de Ejecución',
          body: 'Mi enfoque profesional se basa en el entrenamiento constante. Como el Downhill MTB me ha enseñado, la precisión técnica es el único camino para mantener el control en entornos de alta exigencia. No entiendo el diseño sin la preparación: entreno mi técnica para asegurar que, cuando llega el momento de ejecutar proyectos, la solución sea precisa.'
        },
        {
          title: 'Construyendo Libertad',
          body: 'Creo firmemente en la evolución constante. Hoy, mi enfoque está en la optimización: de interfaces, de sistemas de diseño y de mi propia vida. Estoy en una etapa de curaduría personal, donde cambio lo innecesario por estabilidad, invirtiendo en el futuro para asegurar que mi creatividad siempre tenga un lugar donde aterrizar con propósito.'
        }
      ],

      years: Array.from({ length: 16 }, (_, i) => 2026 - i),

      timelineEvents: {
        2011: {
          company: 'Hospital San Vicente de Paúl',
          role: 'Diseñador de páginas web',
          years: '2011 – 2015 · 4 años',
          desc: 'Primer contacto con UX real. Rediseñé el sitio institucional cuatro veces: ley de transparencia, responsive y accesibilidad. Descubrí que los usuarios necesitaban más.',
          tag: 'Diseño · Web',
          scope: 'Institucional',
          accent: 'var(--milo-forest)',
          impact: '4 rediseños',
          stack: 'HTML · CSS · WP'
        },
        2014: {
          company: 'Cottz',
          role: 'Co-fundador · Diseñador web',
          years: '2014 – 2018 · 4 años',
          desc: 'Emprendí con amigos developers para resolver cotizaciones hospitalarias. Diseñé y desarrollé en MeteorJS. Un proceso de 2 semanas bajó a 1–2 días.',
          tag: 'Startup · Founder',
          scope: 'Healthtech',
          accent: 'var(--milo-amber)',
          impact: '2w → 1–2d',
          stack: 'MeteorJS · UI'
        },
        2015: {
          company: 'Grandpa (Freelance)',
          role: 'UI Designer',
          years: '2015 – 2018 · 3 años',
          desc: 'Productos para GHIX (Suiza), Urgencias.red, HolaDr, Raliz, Hoy Reservas y más. Aprendí a trabajar con equipos distribuidos en múltiples industrias.',
          tag: 'Freelance',
          scope: 'Multi-industria',
          accent: 'var(--milo-indigo)',
          impact: '8+ productos',
          stack: 'Sketch · Figma'
        },
        2018: {
          company: 'La Haus',
          role: 'Product Designer → Lead',
          years: '2018 – 2024 · 6 años 6 meses',
          desc: 'Empecé solo, construí el equipo de diseño. Lideré Design Sprints, plataforma de lanzamientos inmobiliarios al 67% de los ingresos. Golden Path con IA en 2024.',
          tag: 'Proptech · Scale-up',
          scope: 'Lead · IA',
          accent: 'var(--milo-steel)',
          impact: '67% revenue',
          stack: 'Figma · Design Ops'
        },
        2024: {
          company: 'Eden',
          role: 'Senior Product Designer',
          years: 'dic. 2024 – actualidad',
          desc: 'Diseño en el core del sector salud. Eden AI Report, herramientas PET-CT y Cardio PACS. Prototipos realistas en Figma Make con interacciones del mundo real.',
          tag: 'Healthtech · Senior',
          scope: 'AI · Imaging',
          accent: 'var(--milo-terra)',
          impact: 'En curso',
          stack: 'Figma Make · AI'
        }
      }
    }
  },

  mounted() {
    document.body.style.backgroundColor = '#F7F6F2';
    document.body.style.padding = '0';

    this.typeTimer = setTimeout(() => this.tick(), 600);
    this.$nextTick(() => this.updateUnderline());

    this._keyHandler = (e) => {
      if (e.key >= '1' && e.key <= '4') this.selectStrip(Number(e.key) - 1);
    };
    this._resizeHandler = () => this.updateUnderline();
    document.addEventListener('keydown', this._keyHandler);
    window.addEventListener('resize', this._resizeHandler);
  },

  beforeUnmount() {
    document.body.style.backgroundColor = '';
    document.body.style.padding = '';
    clearTimeout(this.typeTimer);
    clearTimeout(this.tipHideTimer);
    document.removeEventListener('keydown', this._keyHandler);
    window.removeEventListener('resize', this._resizeHandler);
  },

  methods: {
    tick() {
      const phrase = this.phrases[this.phraseIndex];
      this.currentAccent = phrase.color;
      if (!this.deleting) {
        this.charIndex++;
        this.displayText = phrase.text.slice(0, this.charIndex);
        if (this.charIndex === phrase.text.length) {
          this.deleting = true;
          this.typeTimer = setTimeout(() => this.tick(), 2200);
        } else {
          this.typeTimer = setTimeout(() => this.tick(), 36);
        }
      } else {
        this.charIndex--;
        this.displayText = phrase.text.slice(0, this.charIndex);
        if (this.charIndex === 0) {
          this.deleting = false;
          this.phraseIndex = (this.phraseIndex + 1) % this.phrases.length;
          this.currentFocus = this.phrases[this.phraseIndex].focus;
          this.typeTimer = setTimeout(() => this.tick(), 320);
        } else {
          this.typeTimer = setTimeout(() => this.tick(), 16);
        }
      }
    },

    onYearEnter(e, year, side, railId) {
      const railRef = railId === 'l' ? this.$refs.railLRef : this.$refs.railRRef;
      const rr = railRef.getBoundingClientRect();
      const rowRect = e.currentTarget.getBoundingClientRect();
      const topOffset = rowRect.top - rr.top - 2;
      if (railId === 'l') {
        this.scrubL.top = topOffset;
        this.scrubL.show = true;
      } else {
        this.scrubR.top = topOffset;
        this.scrubR.show = true;
      }
      if (this.timelineEvents[year]) this.showTip(e, year, side);
    },

    onYearLeave(year) {
      if (this.timelineEvents[year]) this.hideTip();
    },

    hideScrubber(railId) {
      if (railId === 'l') this.scrubL.show = false;
      else this.scrubR.show = false;
    },

    showTip(e, year, side) {
      clearTimeout(this.tipHideTimer);
      const ev = this.timelineEvents[year];
      if (!ev) return;
      const shell = this.$refs.shellRef;
      const shellRect = shell.getBoundingClientRect();
      const rowRect = e.currentTarget.getBoundingClientRect();
      const topGuess = rowRect.top - shellRect.top - 18;
      const top = Math.max(70, Math.min(shellRect.height - 200, topGuess));
      const style = { top: top + 'px' };
      if (side === 'left') { style.left = '72px'; style.right = 'auto'; }
      else { style.right = '72px'; style.left = 'auto'; }
      this.tipData = { show: true, event: ev, side, style };
      this.currentAccent = ev.accent;
      this.currentFocus = ev.company;
    },

    hideTip() {
      this.tipHideTimer = setTimeout(() => {
        this.tipData.show = false;
        const p = this.phrases[this.phraseIndex];
        this.currentAccent = p.color;
        this.currentFocus = p.focus;
      }, 120);
    },

    selectStrip(i) {
      this.activeStrip = i;
      this.$router.replace({ query: i > 0 ? { strip: i } : {} });
      this.$nextTick(() => this.updateUnderline());
    },

    scrollToWork() {
      this.navSelectStrip(0);
    },

    navSelectStrip(i) {
      this.selectStrip(i);
      this.$nextTick(() => {
        const strip = this.$refs.stripRef;
        if (strip) {
          const navH = document.querySelector('.site-nav')?.offsetHeight || 56;
          const top = strip.getBoundingClientRect().top + window.scrollY - navH - 16;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    },

    updateUnderline() {
      const strip = this.$refs.stripRef;
      if (!strip) return;
      const items = strip.querySelectorAll('.strip-item');
      const activeItem = items[this.activeStrip];
      if (!activeItem) return;
      const sr = strip.getBoundingClientRect();
      const ir = activeItem.getBoundingClientRect();
      this.underlineStyle = {
        width: ir.width + 'px',
        transform: `translateX(${ir.left - sr.left}px)`
      };
    },

    resolveAccent(accent) {
      const map = {
        'var(--milo-indigo)': 'oklch(0.52 0.12 280)',
        'var(--milo-forest)': 'oklch(0.52 0.12 165)',
        'var(--milo-terra)':  'oklch(0.55 0.12 45)',
        'var(--milo-steel)':  'oklch(0.52 0.12 245)',
        'var(--milo-amber)':  'oklch(0.60 0.12 75)'
      };
      return map[accent] || accent;
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&family=Inter:wght@300;400;500;600;700&display=swap');

:root {
  --bg: #F7F6F2;
  --bg-2: #FFFFFF;
  --ink: #13130F;
  --ink-2: #2A2A24;
  --muted: #6B6B63;
  --muted-2: #9A9A90;
  --faint: #C9C7BD;
  --line: rgba(19,19,15,0.08);
  --line-2: rgba(19,19,15,0.14);
  --chip-bg: rgba(19,19,15,0.04);

  --milo-indigo: oklch(0.52 0.12 280);
  --milo-indigo-bg: oklch(0.96 0.025 280);
  --milo-forest: oklch(0.52 0.12 165);
  --milo-forest-bg: oklch(0.96 0.025 165);
  --milo-terra: oklch(0.55 0.12 45);
  --milo-terra-bg: oklch(0.96 0.025 45);
  --milo-steel: oklch(0.52 0.12 245);
  --milo-steel-bg: oklch(0.96 0.025 245);
  --milo-amber: oklch(0.60 0.12 75);
  --milo-amber-bg: oklch(0.96 0.025 75);

  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 14px;
}
</style>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.shell {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  padding-top: 54px;
  background: var(--bg);
  color: var(--ink);
  font-family: 'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

/* dotted grid */
.dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, #C8C6BD 1px, transparent 1px);
  background-size: 28px 28px;
  opacity: .5;
  pointer-events: none;
  mask-image: radial-gradient(ellipse 72% 55% at 50% 48%, #000 30%, transparent 85%);
  -webkit-mask-image: radial-gradient(ellipse 72% 55% at 50% 48%, #000 30%, transparent 85%);
}

/* ── NAV ── */
.site-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-bottom: 1px solid var(--line);
  background: color-mix(in oklab, var(--bg) 85%, transparent);
  backdrop-filter: blur(10px) saturate(110%);
  -webkit-backdrop-filter: blur(10px) saturate(110%);
  border-radius: 0;
  border-top: none;
  border-left: none;
  border-right: none;
  width: 100%;
}
.nav-left { display: flex; align-items: center; gap: 10px; }
.brand-stack { display: flex; flex-direction: column; gap: 1px; }
.brand-mark {
  width: 26px; height: 26px; border-radius: 7px;
  background: var(--ink); color: var(--bg);
  display: grid; place-items: center;
  font-family: 'Geist', sans-serif; font-weight: 600; font-size: 13px; line-height: 1; letter-spacing: -0.02em;
}
.brand-name { font-size: 13px; font-weight: 500; letter-spacing: -0.1px; }
.brand-role { font-size: 13px; color: var(--muted); letter-spacing: -0.1px; }
.brand-role { font-size: 11px; color: var(--muted); letter-spacing: -0.1px; line-height: 1; }

.nav-center {
  display: flex; align-items: center; gap: 2px;
  padding: 3px; border-radius: 10px;
  border: 1px solid var(--line); background: var(--bg-2);
}
.nav-tab {
  font-size: 12.5px; color: var(--muted);
  padding: 6px 12px; border-radius: 7px;
  cursor: pointer; transition: all .15s; letter-spacing: -0.1px;
  font-family: inherit; border: none; background: transparent;
}
.nav-tab:hover { color: var(--ink); }
.nav-tab.active { color: var(--ink); background: var(--chip-bg); }

.nav-right { display: flex; align-items: center; gap: 10px; }
.badge {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12px; color: var(--ink-2);
  padding: 5px 10px; border-radius: 999px;
  border: 1px solid var(--line-2); background: var(--bg-2); letter-spacing: -0.1px;
}
.badge-dot {
  width: 6px; height: 6px; border-radius: 50%; background: var(--milo-forest);
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--milo-forest) 25%, transparent);
  animation: pulse 2.4s ease-in-out infinite;
}
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }

.btn {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  padding: 7px 12px; border-radius: 8px; cursor: pointer;
  transition: all .15s; letter-spacing: -0.1px;
}
.btn-ghost { background: transparent; border: 1px solid var(--line-2); color: var(--ink); }
.btn-ghost:hover { background: var(--chip-bg); }
.btn-dark { background: var(--ink); color: var(--bg); border: 1px solid var(--ink); }
.btn-dark:hover { background: #000; }

/* ── RAILS ── */
.rail {
  position: absolute;
  top: 60px;
  bottom: 0;
  width: 64px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 0;
  z-index: 6;
  user-select: none;
}
.rail-l { left: 0; border-right: 1px solid var(--line); }
.rail-r { right: 0; border-left: 1px solid var(--line); }

.rail-head {
  position: absolute; top: -1px; left: 0; right: 0;
  padding: 10px 0; text-align: center;
  font-family: 'Geist Mono', monospace; font-size: 9.5px;
  color: var(--muted-2); letter-spacing: .14em; text-transform: uppercase;
  border-bottom: 1px solid var(--line);
  background: color-mix(in oklab, var(--bg) 70%, transparent);
}

.yr {
  position: relative; width: 100%;
  display: flex; align-items: center;
  cursor: default;
  padding: 2px 0;
  transition: opacity .2s;
}
.rail-l .yr { justify-content: flex-end; padding-right: 10px; }
.rail-r .yr { justify-content: flex-start; padding-left: 10px; }

.yr-label {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; color: var(--faint); font-weight: 400;
  transition: color .15s, font-weight .15s, letter-spacing .15s;
  line-height: 1;
}
.yr.has .yr-label { color: var(--muted); font-weight: 500; }
.yr:hover .yr-label { color: var(--ink); letter-spacing: .02em; }

.yr-tick {
  position: absolute; height: 1px; background: var(--line-2);
  transition: width .2s ease, background .2s ease;
}
.rail-l .yr-tick { right: 0; width: 6px; }
.rail-r .yr-tick { left: 0; width: 6px; }
.yr.has .yr-tick { width: 12px; }
.yr:hover .yr-tick { width: 18px; background: var(--ink); }

.yr-dot {
  position: absolute; width: 7px; height: 7px; border-radius: 50%;
  background: var(--bg); border: 1.25px solid var(--muted-2);
  transition: all .2s ease;
}
.rail-l .yr-dot { right: -4px; }
.rail-r .yr-dot { left: -4px; }
.yr.has:hover .yr-dot {
  background: var(--dot-c, var(--ink));
  border-color: var(--dot-c, var(--ink));
  transform: scale(1.35);
  box-shadow: 0 0 0 4px color-mix(in oklab, var(--dot-c, var(--ink)) 15%, transparent);
}

/* year 2026 extra top padding */
.yr[data-year="2026"] { padding-top: 14px; }

/* scrubber */
.scrubber {
  position: absolute; height: 20px; width: 100%;
  pointer-events: none;
  opacity: 0; transition: opacity .15s, top .18s cubic-bezier(.2,.7,.2,1);
}
.scrubber.show { opacity: 1; }
.scrubber::before {
  content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 22px;
  transform: translateY(-50%);
  background: color-mix(in oklab, var(--ink) 4%, transparent);
  border-radius: 4px;
}

/* ── TOOLTIP ── */
.tip {
  position: absolute; z-index: 30;
  max-width: 320px; min-width: 260px;
  opacity: 0; transform: translateY(4px);
  transition: opacity .18s, transform .18s;
  pointer-events: none;
}
.tip.show { opacity: 1; transform: translateY(0); }
.tip-card {
  background: var(--ink); color: var(--bg);
  border-radius: 14px;
  padding: 14px 14px 12px;
  box-shadow: 0 20px 40px -20px rgba(0,0,0,.35), 0 2px 6px rgba(0,0,0,.08);
  position: relative;
}
.tip-card::before {
  content: ''; position: absolute; width: 8px; height: 8px;
  background: var(--ink); transform: rotate(45deg);
  top: 20px;
}
.tip.left .tip-card::before { right: -3px; }
.tip.right .tip-card::before { left: -3px; }

.tip-tag {
  position: absolute; top: -8px; left: 14px;
  font-size: 9.5px; font-weight: 600; padding: 3px 8px; border-radius: 999px;
  letter-spacing: .05em; text-transform: uppercase;
  font-family: 'Geist Mono', monospace;
}
.tip-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.tip-co {
  font-family: 'Geist Mono', monospace; font-size: 10px;
  color: var(--muted-2); letter-spacing: .12em; text-transform: uppercase;
}
.tip-scope-badge {
  font-family: 'Geist Mono', monospace; font-size: 9.5px;
  padding: 2px 7px; border-radius: 999px;
  background: rgba(255,255,255,.08); color: #E4E1D2;
  letter-spacing: .08em; text-transform: uppercase;
}
.tip-role {
  font-family: 'Geist', 'Inter', sans-serif; font-weight: 600; font-size: 16px; line-height: 1.25;
  color: #FAFAF4; letter-spacing: -0.02em; margin-bottom: 4px;
}
.tip-years { font-size: 11.5px; color: #A9A69B; margin-bottom: 10px; font-family: 'Geist Mono', monospace; }
.tip-desc { font-size: 12.5px; line-height: 1.55; color: #CFCCBE; margin-bottom: 12px; }
.tip-meta {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
  padding-top: 10px; border-top: 1px solid rgba(255,255,255,.08);
}
.tip-meta .cell .k {
  font-family: 'Geist Mono', monospace; font-size: 9.5px;
  color: #7E7B71; letter-spacing: .1em; text-transform: uppercase;
  display: block; margin-bottom: 3px;
}
.tip-meta .cell .v { font-size: 12px; color: #EDEAD9; font-weight: 500; font-family: 'Geist Mono', monospace; }

/* ── HERO ── */
.hero {
  position: relative;
  padding: 64px 24px 40px;
  display: flex; flex-direction: column; align-items: center;
}

.eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 11px; font-weight: 500; letter-spacing: .12em; text-transform: uppercase;
  color: var(--muted-2);
  padding: 6px 12px; border-radius: 999px;
  border: 1px solid var(--line); background: var(--bg-2);
  font-family: 'Geist Mono', monospace;
}
.eb-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--faint); }

.avatar-ring {
  margin-top: 28px; width: 94px; height: 94px; border-radius: 50%;
  padding: 3px; background: var(--bg-2);
  border: 1px solid var(--line);
  box-shadow: 0 1px 2px rgba(0,0,0,.02), 0 8px 24px -12px rgba(0,0,0,.08);
  overflow: hidden;
  position: relative;
}
.avatar-inner {
  position: absolute;
  width: 274%; height: auto;
  left: -92%;
  top: -30%;
  border-radius: 0;
  display: block;
}

.headline {
  margin-top: 22px;
  text-align: center;
  max-width: 860px;
}
.h-line1 {
  display: block;
  font-family: 'Geist', 'Inter', sans-serif; font-weight: 500;
  font-size: clamp(38px, 5.2vw, 72px);
  line-height: 1.02; letter-spacing: -0.04em;
  color: var(--ink);
}
.h-line2 {
  display: block; margin-top: 6px;
  height: 2.42em; /* fixed: 2 lines × 1.1 line-height + small buffer */
  overflow: hidden;
  font-family: 'Geist', 'Inter', sans-serif; font-weight: 500;
  font-size: clamp(38px, 5.2vw, 72px);
  line-height: 1.1; letter-spacing: -0.04em;
}
.tcursor {
  display: inline-block; width: 2px; height: 0.78em; vertical-align: -2px;
  margin-left: 4px; border-radius: 1px;
  animation: blink .8s steps(1) infinite;
}
@keyframes blink { 50% { opacity: 0; } }

.subtitle {
  margin-top: 26px; max-width: 520px; text-align: center;
  font-size: 15px; line-height: 1.6; color: var(--muted); font-weight: 400;
}

.cta-row { display: flex; align-items: center; gap: 10px; margin-top: 28px; }
.btn-primary {
  background: var(--ink); color: var(--bg); border: 1px solid var(--ink);
  padding: 11px 18px; border-radius: 10px; font-size: 13.5px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer; font-family: inherit;
  transition: transform .15s ease, background .15s ease; letter-spacing: -0.1px;
}
.btn-primary:hover { transform: translateY(-1px); background: #000; }
.btn-primary svg { transition: transform .2s ease; }
.btn-primary:hover svg { transform: translateX(3px); }
.btn-outline {
  background: var(--bg-2); border: 1px solid var(--line-2); color: var(--ink);
  padding: 11px 16px; border-radius: 10px; font-size: 13.5px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer; font-family: inherit;
  letter-spacing: -0.1px; transition: background .15s;
}
.btn-outline:hover { background: var(--chip-bg); }

.focused {
  margin-top: 22px; display: inline-flex; align-items: center; gap: 10px;
  padding: 5px 6px 5px 12px; border-radius: 999px;
  border: 1px solid var(--line); background: var(--bg-2);
  font-size: 12px; color: var(--muted); letter-spacing: -0.1px;
}
.chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 8px; border-radius: 999px;
  background: var(--chip-bg); font-weight: 500;
  font-family: 'Geist Mono', monospace; font-size: 11px;
}
.cdot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; opacity: .6; }

.proof { margin-top: 34px; display: flex; align-items: center; gap: 12px; }
.avatars { display: flex; }
.av {
  width: 26px; height: 26px; border-radius: 50%;
  border: 2px solid var(--bg); margin-left: -6px;
  font-size: 10.5px; font-weight: 500; display: grid; place-items: center;
  font-family: 'Geist Mono', monospace;
}
.av:first-child { margin-left: 0; }
.av-a { background: var(--milo-indigo-bg); color: var(--milo-indigo); }
.av-b { background: var(--milo-forest-bg); color: var(--milo-forest); }
.av-c { background: var(--milo-terra-bg); color: var(--milo-terra); }
.av-d { background: var(--milo-steel-bg); color: var(--milo-steel); }
.proof-text { font-size: 12.5px; color: var(--muted-2); }
.proof-text b { color: var(--ink); font-weight: 500; }

/* hero entrance animation */
@keyframes up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
.hero > * { animation: up .6s cubic-bezier(.2,.7,.2,1) both; }
.hero > *:nth-child(1) { animation-delay: .05s; }
.hero > *:nth-child(2) { animation-delay: .12s; }
.hero > *:nth-child(3) { animation-delay: .2s; }
.hero > *:nth-child(4) { animation-delay: .28s; }
.hero > *:nth-child(5) { animation-delay: .36s; }
.hero > *:nth-child(6) { animation-delay: .44s; }
.hero > *:nth-child(7) { animation-delay: .52s; }

/* ── STRIP ── */
.strip {
  position: relative; z-index: 5;
  border-top: 1px solid var(--line);
  display: grid; grid-template-columns: repeat(3, 1fr);
  background: color-mix(in oklab, var(--bg) 75%, #fff);
  margin: 0 64px;
}
.strip-item {
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start; gap: 14px;
  padding: 22px 22px 24px; cursor: pointer;
  border-right: 1px solid var(--line);
  transition: background .15s;
}
.strip-item:last-child { border-right: none; }
.strip-item:hover { background: rgba(255,255,255,.6); }
.strip-item.active { background: var(--bg-2); }

.strip-underline {
  position: absolute; left: 0; bottom: -1px; height: 1.5px; background: var(--ink);
  transition: transform .3s cubic-bezier(.2,.7,.2,1), width .3s cubic-bezier(.2,.7,.2,1);
}

.si-ic {
  width: 34px; height: 34px; border-radius: 9px;
  display: grid; place-items: center; flex-shrink: 0;
  border: 1px solid var(--line);
}
.si-ic.indigo { background: var(--milo-indigo-bg); color: var(--milo-indigo); }
.si-ic.forest { background: var(--milo-forest-bg); color: var(--milo-forest); }
.si-ic.terra  { background: var(--milo-terra-bg);  color: var(--milo-terra); }
.si-ic.steel  { background: var(--milo-steel-bg);  color: var(--milo-steel); }

.si-text { display: flex; flex-direction: column; gap: 3px; }
.si-text .t1 { display: block; font-size: 13.5px; font-weight: 500; color: var(--ink); letter-spacing: -0.01em; line-height: 1.25; }
.si-text .t2 { display: block; font-size: 12px; color: var(--muted-2); line-height: 1.3; }
.strip-item:not(.active) .si-text .t1 { color: var(--ink-2); }

.si-arrow {
  position: absolute; top: 22px; right: 18px;
  opacity: 0; transform: translateX(-4px);
  transition: all .15s; color: var(--muted);
}
.strip-item:hover .si-arrow { opacity: 1; transform: translateX(0); }

/* ── PROJECTS SECTION ── */
.projects-section {
  margin: 0 64px;
  padding: 56px 40px 80px;
  border-top: 1px solid var(--line);
}

.ps-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 36px;
}
.ps-header-left { display: flex; flex-direction: column; gap: 6px; }
.ps-label {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; font-weight: 500;
  letter-spacing: .14em; text-transform: uppercase;
  color: var(--muted-2);
}
.ps-title {
  font-family: 'Geist', 'Inter', sans-serif;
  font-size: clamp(24px, 3vw, 36px); font-weight: 500;
  letter-spacing: -0.03em; color: var(--ink); line-height: 1.1;
}
.ps-desc {
  font-size: 13.5px; color: var(--muted); line-height: 1.6;
  max-width: 360px; text-align: right;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.project-card {
  position: relative;
  border-radius: var(--radius-lg);
  border: 1px solid var(--line);
  background: var(--bg-2);
  overflow: hidden;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  display: flex;
  flex-direction: column;
}
.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px -12px rgba(0,0,0,.1), 0 2px 6px rgba(0,0,0,.04);
  border-color: var(--line-2);
}
.project-card:hover .pc-arrow { opacity: 1; transform: translate(0, 0); }

/* Visual area */
.pc-visual {
  position: relative;
  height: 260px;
  overflow: hidden;
  flex-shrink: 0;
}
.pc-visual--thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.pc-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pc-visual-dots {
  position: absolute; inset: 0;
  background-image: radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px);
  background-size: 20px 20px;
}
.pc-visual-label {
  position: absolute; bottom: 16px; left: 16px;
  font-family: 'Geist Mono', monospace;
  font-size: 11px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
}
.pc-company-tag { opacity: .7; }
.pc-visual-year {
  position: absolute; top: 14px; right: 14px;
  font-family: 'Geist Mono', monospace;
  font-size: 9.5px; color: var(--muted-2);
  letter-spacing: .05em;
  background: rgba(255,255,255,.7);
  padding: 3px 8px; border-radius: 999px;
  backdrop-filter: blur(4px);
}

/* Card body */
.pc-body {
  padding: 20px 20px 24px;
  display: flex; flex-direction: column; gap: 8px;
  flex: 1;
}
.pc-meta {
  display: flex; align-items: center; gap: 6px;
  font-family: 'Geist Mono', monospace; font-size: 10px;
  color: var(--muted-2); letter-spacing: .06em;
}
.pc-sep { opacity: .4; }
.pc-title {
  font-size: 15px; font-weight: 500; color: var(--ink);
  letter-spacing: -0.02em; line-height: 1.3;
}
.pc-desc {
  font-size: 12.5px; line-height: 1.6; color: var(--muted);
  flex: 1;
}
.pc-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.pc-tag {
  font-family: 'Geist Mono', monospace; font-size: 10px; font-weight: 500;
  padding: 3px 8px; border-radius: 999px;
  letter-spacing: .04em;
}

/* Hover arrow */
.pc-arrow {
  position: absolute; top: 16px; right: 16px;
  color: var(--muted-2);
  opacity: 0; transform: translate(-4px, 4px);
  transition: all .2s ease;
}

/* responsive */
@media (max-width: 1100px) and (min-width: 601px) {
  .projects-grid { grid-template-columns: repeat(2, 1fr); }
  .about-section { margin: 0 40px !important; }
  .ab-grid { grid-template-columns: 1fr !important; }
  .ab-block { padding: 24px 0 !important; border-right: none !important; border-bottom: 1px solid var(--line) !important; }
  .ab-block:nth-child(even) { padding-left: 0 !important; }
  .ab-block:last-child { border-bottom: none !important; }
}

@media (max-width: 900px) {
  .rail { display: none; }
  .strip { margin: 0; grid-template-columns: 1fr; }
  .strip-item { border-right: none; border-bottom: 1px solid var(--line); }
  .nav-center { display: none; }
  .projects-section { margin: 0; padding: 40px 20px 60px; }
  .ps-header { flex-direction: column; align-items: flex-start; }
  .ps-desc { text-align: left; max-width: 100%; }
  .projects-grid { grid-template-columns: 1fr; }

  .about-section { margin: 0; padding: 40px 20px 60px; }
  .ab-header { flex-direction: column; align-items: flex-start; gap: 28px; }
  .ab-stats { width: 100%; grid-template-columns: repeat(3, 1fr); }
  .ab-stat:last-child { border-right: none; }
  .ab-grid { grid-template-columns: 1fr !important; }
  .ab-block { padding: 24px 0; border-right: none !important; border-bottom: 1px solid var(--line); }
  .ab-block:last-child { border-bottom: none; }
}

@media (max-width: 600px) {
  .about-section { margin: 0 !important; padding: 40px !important; }
  .ab-block { margin-top: 0; padding: 24px 0 !important; border-right: none !important; border-bottom: 1px solid var(--line) !important; }
  .ab-block:nth-child(even) { padding-left: 0 !important; }
  .ab-block:last-child { border-bottom: none !important; }
}

@media (max-width: 478px) {
  .h-line1, .h-line2 { font-size: 24px !important; }
}

@media (max-width: 500px) {
  .about-section { margin: 0 !important; padding: 24px !important; }
  .ab-grid { grid-template-columns: 1fr !important; }
  .ab-block { border-right: none !important; padding: 0 !important; padding-top: 40px !important; }
  .ab-block:first-child { padding-top: 0 !important; }
}

/* ── ABOUT SECTION ── */
.about-section {
  margin: 0 64px;
  padding: 24px;
  border-top: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ab-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 40px;
}
.ab-header-identity {
  display: flex;
  align-items: center;
  gap: 20px;
}
.ab-photo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--line);
  flex-shrink: 0;
}
.ab-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ab-name {
  font-size: clamp(22px, 2.5vw, 30px);
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--ink);
  line-height: 1.15;
  margin: 0;
}
.ab-role {
  font-size: 13px;
  color: var(--muted);
  letter-spacing: -0.01em;
}

.ab-stats {
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 0;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
}
.ab-stat {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 16px 22px;
  border-right: 1px solid var(--line);
}
.ab-stat:last-child { border-right: none; }
.ab-stat-val {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.04em;
  color: var(--ink);
  line-height: 1;
}
.ab-stat-label {
  font-family: 'Geist Mono', monospace;
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--muted-2);
  white-space: nowrap;
}

.ab-divider {
  height: 1px;
  background: var(--line);
  margin-bottom: 0;
}

.ab-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
.ab-block {
  padding: 36px 40px 36px 0;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ab-block:nth-child(even) {
  padding-left: 40px;
  padding-right: 0;
  border-right: none;
}
.ab-block:nth-child(3),
.ab-block:nth-child(4) {
  border-bottom: none;
}
.ab-num {
  font-family: 'Geist Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: .1em;
  color: var(--muted-2);
}
.ab-block-title {
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.025em;
  color: var(--ink);
  line-height: 1.2;
  margin: 0;
}
.ab-block-body {
  font-size: 14px;
  line-height: 1.72;
  color: var(--muted);
  max-width: 440px;
}
</style>
